import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutGrid, Settings, LogOut, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const navItem = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive
                ? "bg-brand/15 text-brand"
                : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
        }`;

    return (
        <div className="dark min-h-screen flex bg-zinc-950 text-zinc-100">
            <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-zinc-950">
                <div className="p-6 flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-brand flex items-center justify-center text-white">
                        <Zap className="h-5 w-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display font-bold leading-tight text-zinc-100">DinePulse</div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Admin</div>
                    </div>
                </div>

                <nav className="px-3 space-y-1 flex-1">
                    <NavLink to="/admin" end className={navItem} data-testid="nav-dashboard">
                        <LayoutGrid className="h-4 w-4" />
                        Businesses
                    </NavLink>
                    <NavLink to="/admin/settings" className={navItem} data-testid="nav-settings">
                        <Settings className="h-4 w-4" />
                        Settings
                    </NavLink>
                </nav>

                <div className="p-3 border-t border-white/10">
                    <div className="px-3 py-2 mb-2">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Signed in</div>
                        <div className="text-sm truncate text-zinc-200">{user?.email}</div>
                    </div>
                    <Button
                        onClick={handleLogout}
                        variant="ghost"
                        className="w-full justify-start text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
                        data-testid="logout-button"
                    >
                        <LogOut className="h-4 w-4 me-2" />
                        Sign out
                    </Button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-zinc-950/85 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-lg bg-brand flex items-center justify-center text-white">
                        <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </div>
                    <span className="font-display font-bold text-zinc-100">DinePulse Admin</span>
                </div>
                <div className="flex items-center gap-1">
                    <NavLink to="/admin" end className="p-2 text-zinc-400" data-testid="mobile-nav-dashboard">
                        <LayoutGrid className="h-5 w-5" />
                    </NavLink>
                    <NavLink to="/admin/settings" className="p-2 text-zinc-400" data-testid="mobile-nav-settings">
                        <Settings className="h-5 w-5" />
                    </NavLink>
                    <button
                        onClick={handleLogout}
                        className="p-2 text-zinc-400"
                        data-testid="mobile-logout-button"
                        aria-label="Sign out"
                    >
                        <LogOut className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <main className="flex-1 md:pt-0 pt-14 min-w-0">
                <Outlet />
            </main>
        </div>
    );
}
